import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  GiftcardCategoryState,
  GiftcardCategoryResponse,
  GiftcardCategory,
} from "./types/gift-types";
import { PaginationReturn, TextStatusSearchParams } from "./types/types";

const initialState: GiftcardCategoryState = {
  giftcardCategories: [],
  giftcardCategory: null,
  selectedGiftcardCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE DataCategory (supports FormData for image upload)
export const createGiftcardCategory = createAsyncThunk<
  GiftcardCategoryResponse,
  FormData | Omit<GiftcardCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("giftCategories/create", async (giftcardCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = giftcardCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/gift-cards-categories`,
      giftcardCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return {
      message: res.data.message,
      dataCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || {
        message: "Failed to create gift card plan category",
      },
    );
  }
});

// READ (fetch all) DataCategories (with pagination)
export const fetchGiftcardCategories = createAsyncThunk<
  GiftcardCategoryResponse & PaginationReturn,
  TextStatusSearchParams,
  { rejectValue: ApiError; state: RootState }
>("giftCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const {
      url,
      perPage = 15,
      search,
      status,
      ...queryParams
    } = urlFilters || {};

    const headers = { Authorization: `Bearer ${token}` };

    let res;

    if (url) {
      res = await axios.get(url, { headers });
    } else {
      const params: Record<string, any> = {};

      if (search) params.search = search;
      if (status) params.status = status;
      if (queryParams.page) params.page = queryParams.page;
      if (perPage) params.per_page = perPage;

      res = await axios.get(`${API_URL}/gift-cards-categories`, {
        params,
        headers,
      });
    }

    return {
      message: res.data.data.message,
      giftcardCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || {
        message: "Failed to fetch gift card plan categories",
      },
    );
  }
});

// READ (fetch one) DataCategory by ID
export const fetchGiftcardCategoryById = createAsyncThunk<
  GiftcardCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("giftCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/gift-cards-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || {
        message: "Failed to fetch gift card plan category",
      },
    );
  }
});

// UPDATE DataCategory (supports FormData for image upload)
export const updateGiftcardCategory = createAsyncThunk<
  GiftcardCategory,
  { id: number; data: Partial<GiftcardCategory> | FormData },
  { rejectValue: ApiError; state: RootState }
>("giftCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/gift-cards-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      },
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || {
        message: "Failed to update gift card plan category",
      },
    );
  }
});

// DELETE DataCategory
export const deleteGiftcardCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("giftcardCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/gift-cards-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || {
        message: "Failed to delete gift card plan category",
      },
    );
  }
});

// Slice
const giftcardCategorySlice = createSlice({
  name: "giftcardCategories",
  initialState,
  reducers: {
    resetGiftcardCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedGiftcardCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createGiftcardCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createGiftcardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.giftcardCategory) {
          state.giftcardCategories.unshift(action.payload.giftcardCategory);
        }
      })
      .addCase(createGiftcardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create gift card plan category";
      })

      // Fetch All
      .addCase(fetchGiftcardCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGiftcardCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.giftcardCategories = action.payload.giftcardCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchGiftcardCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch gift card categories";
      })

      // Fetch One
      .addCase(fetchGiftcardCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedGiftcardCategory = null;
      })
      .addCase(fetchGiftcardCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGiftcardCategory = action.payload;
      })
      .addCase(fetchGiftcardCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch gift card category";
      })

      // Update
      .addCase(updateGiftcardCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGiftcardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.giftcardCategories = state.giftcardCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category,
        );
        if (state.selectedGiftcardCategory?.id === action.payload.id) {
          state.selectedGiftcardCategory = action.payload;
        }
      })
      .addCase(updateGiftcardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update gift card category";
      })

      // Delete
      .addCase(deleteGiftcardCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteGiftcardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.giftcardCategories = state.giftcardCategories.filter(
          (category) => category.id !== action.payload,
        );
      })
      .addCase(deleteGiftcardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete gift card category";
      });
  },
});

export const { resetGiftcardCategoryState } = giftcardCategorySlice.actions;
export default giftcardCategorySlice.reducer;
